import { resolveDeviceInfoVisibility } from "shared/helpers/device-info/resolveDeviceInfoVisibility";
import { isJsonObject } from "shared/helpers/isJsonObject";
import {
  type DeviceInfo,
  type DeviceInfoVisibilityConfig,
  isDeviceInfoVisibilityConfig,
} from "shared/types/DeviceInfo";

/** The landing page used when the Hub was opened at `/` or after login. */
export const FALLBACK_DEFAULT_APP_ROUTE = "/overview";

/**
 * One row of the "Select Default Route" table: a route and the platforms and
 * screens it is the landing page for.
 */
export type DefaultAppRoutePlatformRow = DeviceInfoVisibilityConfig & {
  /** The route's URL, e.g. `/conference/schedule`. */
  route: string;
};

/** The `default_app_route` society setting. */
export type DefaultAppRouteSetting = {
  /** Where members land when no platform row matches their device. */
  defaultRoute: string;
  /** Checked top to bottom; the first row that matches the device wins. */
  platformConfig: DefaultAppRoutePlatformRow[];
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const isDefaultAppRoutePlatformRow = (
  value: unknown,
): value is DefaultAppRoutePlatformRow =>
  isJsonObject(value) &&
  isNonEmptyString(value.route) &&
  isDeviceInfoVisibilityConfig(value);

export const isDefaultAppRouteSetting = (
  value: unknown,
): value is DefaultAppRouteSetting =>
  isJsonObject(value) &&
  isNonEmptyString(value.defaultRoute) &&
  Array.isArray(value.platformConfig) &&
  value.platformConfig.every(isDefaultAppRoutePlatformRow);

/**
 * Reads the stored `default_app_route` value.
 *
 * - The object form: `{ defaultRoute, platformConfig }`.
 * - A plain URL such as `/overview`, which is how the setting was stored before
 *   it became an object. Kept so a society whose row was not migrated, or a
 *   cached value, still lands where it did.
 *
 * Anything else, including text that is not JSON, reads as `undefined` rather
 * than throwing: the value is read while building every Hub session.
 */
export const parseDefaultAppRouteSetting = (
  value: string | undefined | null,
): DefaultAppRouteSetting | undefined => {
  if (!value) {
    return undefined;
  }
  if (value.startsWith("/")) {
    return { defaultRoute: value, platformConfig: [] };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }
  return isDefaultAppRouteSetting(parsed) ? parsed : undefined;
};

type ResolveDefaultAppRouteProps = {
  /** The society's `default_app_route` setting, if it has one. */
  setting?: DefaultAppRouteSetting;
  /** The device the member is on. */
  device: DeviceInfo;
};

/**
 * The route a member lands on. Platform rows are checked top to bottom and the
 * first one that matches the device wins - a row matches when the same rules as
 * the back button say "show" for it, so a row with no platform checked never
 * matches. With no match, `defaultRoute`; with no setting, `/overview`.
 */
export const resolveDefaultAppRoute = ({
  setting,
  device,
}: ResolveDefaultAppRouteProps): string => {
  const matchingRow = setting?.platformConfig.find(
    (row) => resolveDeviceInfoVisibility({ device, config: row }) === true,
  );
  return (
    matchingRow?.route ?? setting?.defaultRoute ?? FALLBACK_DEFAULT_APP_ROUTE
  );
};
